import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Line, LineChart, XAxis, YAxis, CartesianGrid } from "recharts";

import { FeedPageHeader, FeedPageTitle } from "@/components/FeedPageHeader";
import { Page } from "@/components/Page";
import { PreviewPost } from "@/components/PreviewFeed";
import { FeedAvatar } from "@/components/ui/avatar";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  feedQueryOptions,
  getHydratedPostQueryOptions,
  StickyPostPerformanceEntry,
  stickyPostPerformanceQueryOptions,
} from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute(
  "/app/_sidebar-layout/feed/$feedId/post-analytics",
)({
  component: RouteComponent,
  validateSearch: (search): { uri: string } => {
    if (!search?.uri) {
      throw Error("No URI provided");
    }

    return {
      uri: search?.uri as string,
    };
  },
  loader: async ({
    context: { queryClient },
    params: { feedId },
    location: { search },
  }) => {
    await Promise.all([
      queryClient.ensureQueryData(
        getHydratedPostQueryOptions((search as { uri: string }).uri),
      ),
      queryClient.ensureQueryData(feedQueryOptions(parseInt(feedId))),
      queryClient.ensureQueryData(
        stickyPostPerformanceQueryOptions({
          feedId: parseInt(feedId),
          uri: (search as { uri: string }).uri,
        }),
      ),
    ]);
  },
  head: () => ({ meta: [{ title: "Post Analytics | Graze" }] }),
});

function PostTimeline({ usage }: { usage: StickyPostPerformanceEntry[] }) {
  return (
    <ChartContainer
      config={{
        daily_total_views: {
          label: "Views",
          color: "bg-cyan-10 dark:bg-cyandark-10",
        },
        daily_unique_user_views: {
          label: "Unique Users",
          color: "bg-grass-10 dark:bg-grassdark-10",
        },
        date: {
          label: "Date",
        },
      }}
      className="w-full h-full flex-4"
    >
      <LineChart data={usage} className="h-full w-full">
        <CartesianGrid strokeDasharray="3 3" />
        <ChartTooltip content={<ChartTooltipContent />} />
        <YAxis
          tick={{ transform: "translate(-8, 0)" }}
          axisLine={false}
          tickLine={false}
        />
        <XAxis
          dataKey="date"
          className="p-10"
          tickFormatter={(v) => {
            return Intl.DateTimeFormat("en-us", {
              year: "2-digit",
              month: "2-digit",
              day: "2-digit",
            }).format(new Date(v as string));
          }}
          tick={{ transform: "translate(0, 12)" }}
          axisLine={false}
          tickLine={false}
        />
        <Line
          type="monotone"
          className="text-cyan-10 dark:text-cyandark-10"
          dataKey="daily_total_views"
          strokeWidth={4}
          stroke="currentColor"
        />
        <Line
          type="monotone"
          className="text-grass-10 dark:text-grassdark-10"
          dataKey="daily_unique_user_views"
          strokeWidth={4}
          stroke="currentColor"
        />
      </LineChart>
    </ChartContainer>
  );
}

function Post() {
  const { uri } = Route.useSearch();
  const { data: post } = useSuspenseQuery(getHydratedPostQueryOptions(uri));

  return (
    <div className="rounded-lg border border-slate-7 dark:border-slatedark-7 bg-slate-subtle p-2">
      <PreviewPost record={post} />
    </div>
  );
}

const colors = {
  green:
    "bg-green-subtle text-green-dim border-green-6 dark:border-greendark-6",
  cyan: "bg-cyan-subtle text-cyan-dim border-cyan-6 dark:border-cyandark-6",
};

function BigNumber({
  value,
  title,
  variant = "green",
}: {
  value: number;
  title: string;
  variant?: keyof typeof colors;
}) {
  return (
    <div
      className={cn(
        "p-4 rounded-lg",
        "border",
        "flex flex-col gap-2",
        "responsive-header shrink-0 w-max",
        colors[variant],
      )}
      style={{
        flexBasis: "25%",
        containerName: "analytics-card",
      }}
    >
      <div className="text-lg font-semibold w-max">{title}</div>
      <div className="text-4xl md:text-6xl xl:text-[7vw] font-bold analytics-value text-slate-normal">
        {Intl.NumberFormat("en-us", {
          notation: "compact",
          maximumSignificantDigits: 3,
        }).format(value)}
      </div>
    </div>
  );
}

function RouteComponent() {
  const { feedId } = Route.useParams();
  const { data: feed } = useSuspenseQuery(feedQueryOptions(parseInt(feedId)));
  const { uri } = Route.useSearch();
  const { data: usage } = useSuspenseQuery(
    stickyPostPerformanceQueryOptions({ feedId: parseInt(feedId), uri }),
  );
  const totalViews = usage.reduce((acc, cur) => acc + cur.daily_total_views, 0);
  const uniqueUserViews = usage.reduce(
    (acc, cur) => acc + cur.daily_unique_user_views,
    0,
  );

  return (
    <Page>
      <FeedPageHeader>
        <FeedPageTitle
          title={
            <div>
              <div className="text-base font-light text-slate-dim">
                {feed.display_name || "Untitled Feed"}
              </div>
              <div>Post Analytics</div>
            </div>
          }
          icon={
            <FeedAvatar
              feed={feed}
              className="h-full w-full rounded-none border-none"
            />
          }
        />
      </FeedPageHeader>
      <div className="flex flex-col gap-4 md:gap-6">
        <div className="flex flex-wrap gap-4 md:gap-6">
          <Post />
          <BigNumber value={totalViews} title="Total Views" variant="cyan" />
          <BigNumber value={uniqueUserViews} title="Unique Users" />
        </div>

        <div className="flex-1 bg-slate-subtle rounded-lg py-4 pr-12 border border-slate-7 dark:border-slatedark-7 flex justify-center items-center">
          <div className="w-full h-full">
            <PostTimeline usage={usage} />
          </div>
        </div>
      </div>
    </Page>
  );
}
